import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Heart,
  Sparkles,
  Award,
  Users,
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  Instagram,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const values = [
  {
    icon: Heart,
    title: 'Made with Love',
    description:
      'Every piece is crafted with genuine care and intention, treating each creation as if it were for our own family.',
  },
  {
    icon: Sparkles,
    title: 'Blessed Craftsmanship',
    description:
      'We begin each project with Bismillah, infusing barakah into every stitch and detail.',
  },
  {
    icon: Award,
    title: 'Premium Quality',
    description:
      'Only the finest materials - pure silks, genuine pearls, and premium threads for lasting beauty.',
  },
  {
    icon: Users,
    title: 'Personal Touch',
    description:
      'We work closely with each bride to understand their vision and create something truly unique.',
  },
];

const stats = [
  { value: '500+', label: 'Happy Brides' },
  { value: '4.9', label: 'Rating' },
  { value: '3+', label: 'Years' },
  { value: '100%', label: 'Handmade' },
];

export default function AboutPage() {
  const storyRef = useRef(null);
  const valuesRef = useRef(null);
  const contactRef = useRef(null);
  const storyInView = useInView(storyRef, { once: true, margin: '-100px' });
  const valuesInView = useInView(valuesRef, { once: true, margin: '-100px' });
  const contactInView = useInView(contactRef, { once: true, margin: '-100px' });

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-fabric" />
        <div className="absolute inset-0 bg-fabric-texture" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-sm text-silver font-sans tracking-wider uppercase mb-4 block">
              Our Story
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground leading-tight mb-6">
              Preserving <span className="text-gradient-silver">Nikah</span> Memories
              <br />with Love & Barakah
            </h1>
            <p className="text-lg text-muted-foreground font-body leading-relaxed">
              Where sacred moments meet timeless craftsmanship
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section ref={storyRef} className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&q=80"
                  alt="Bridal craftsmanship"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Decorative Frame */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-silver/20 rounded-2xl -z-10" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground">
                A Journey of Faith & Creativity
              </h2>
              <div className="space-y-4 text-muted-foreground font-body leading-relaxed">
                <p>
                  Fazila Creations began with a simple belief: that the most sacred
                  moments in life deserve to be preserved with beauty and intention.
                </p>
                <p>
                  Our founder, inspired by her own nikah experience, realized that
                  bridal wear could be more than just fabric — it could carry the
                  weight of prayers, the beauty of calligraphy, and the blessing of
                  meaningful words.
                </p>
                <p>
                  Today, every piece that leaves our workshop carries this same
                  spirit: handcrafted with duas, embellished with love, and designed
                  to make every bride feel truly honored on her special day.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 pt-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <span className="font-display text-2xl text-silver-light block">
                      {stat.value}
                    </span>
                    <span className="text-xs text-muted-foreground">{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-16 md:py-24 bg-gradient-burgundy-soft">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm text-silver font-sans tracking-wider uppercase mb-2 block">
              What We Stand For
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-foreground">
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card border border-border/50 rounded-2xl p-6 text-center hover-glow"
                >
                  <div className="h-14 w-14 rounded-xl bg-muted/50 flex items-center justify-center mx-auto mb-5">
                    <Icon className="h-6 w-6 text-silver" />
                  </div>
                  <h3 className="font-display text-lg text-silver-light mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} id="contact" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={contactInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-sm text-silver font-sans tracking-wider uppercase mb-2 block">
              Get in Touch
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
              We'd Love to Hear From You
            </h2>
            <p className="text-muted-foreground font-body max-w-md mx-auto">
              Have questions about customization? Want to discuss a special order?
              Reach out to us!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h3 className="font-display text-xl text-silver-light mb-6">
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <a
                    href="tel:+919111841033"
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50 hover:border-silver/30 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Phone className="h-5 w-5 text-silver" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="text-silver-light">+91 9111841033</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/919111841033"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50 hover:border-silver/30 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-lg bg-[#25D366]/20 flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-[#25D366]" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">WhatsApp</p>
                      <p className="text-silver-light">+91 9111841033</p>
                    </div>
                  </a>

                  <a
                    href="mailto:hello@fazilacreations.com"
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50 hover:border-silver/30 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Mail className="h-5 w-5 text-silver" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="text-silver-light">hello@fazilacreations.com</p>
                    </div>
                  </a>

                  <a
                    href="https://instagram.com/fazila_creations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border/50 hover:border-silver/30 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Instagram className="h-5 w-5 text-silver" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Instagram</p>
                      <p className="text-silver-light">@fazila_creations</p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <form
                className="bg-card rounded-2xl border border-border/50 p-6 md:p-8 space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const message = encodeURIComponent(
                    `Name: ${formData.get('name')}\nEmail: ${formData.get('email')}\n\nMessage:\n${formData.get('message')}`
                  );
                  window.open(`https://wa.me/919111841033?text=${message}`, '_blank');
                }}
              >
                <div>
                  <label className="block text-sm text-silver-light mb-2">Name</label>
                  <Input
                    name="name"
                    variant="luxury"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-silver-light mb-2">Email</label>
                  <Input
                    name="email"
                    type="email"
                    variant="luxury"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-silver-light mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    variant="luxury"
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    required
                  />
                </div>
                <Button type="submit" variant="ivory" size="lg" className="w-full">
                  Send Message
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
